import React from 'react';

import { NormalizedButton } from 'lib/button';
import { focusOutlineColors } from 'lib/core';
import { IconVisibility, IconVisibilityOff } from 'lib/icons';
import { removeObjectProperties } from 'lib/utilities';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const StyledSuffixButton = styled(NormalizedButton)`
  display: inline-flex;
  padding: 0;
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.3s ease;
  svg {
    margin: 4px 0 0 4px; /* centers icon inside button */
  }
  &:hover {
    background-color: ${({ theme }) => theme.color.primary[100].value};
  }
  &:focus {
    background-color: ${({ theme }) => theme.color.primary[100].value};
    box-shadow: 0 0 0 4px ${({ theme }) => focusOutlineColors.getPrimary(theme)};
    transition: box-shadow 0.3s ease;
  }
  &:active {
    background-color: ${({ theme }) => theme.color.primary[200].value};
    box-shadow: none;
  }
  &:disabled {
    cursor: not-allowed;
    background-color: transparent;
  }
`;

const ShowHideValueToggleButton = ({
  dataTestId = 'button-icon',
  isDisabled = false,
  isHidden = false,
  onToggleShowHide,
  toggleHideLabel = 'Show value',
  toggleShowLabel = 'Hide value',
  ...other
}) => {
  const Icon = isHidden ? IconVisibility : IconVisibilityOff;
  const toggleLabel = isHidden ? toggleHideLabel : toggleShowLabel;

  const filteredProps = removeObjectProperties(other, 'dataTestId');

  return (
    <StyledSuffixButton
      aria-label={toggleLabel}
      data-testid={dataTestId}
      disabled={isDisabled}
      onClick={onToggleShowHide}
      type="button"
      {...filteredProps}
    >
      <Icon />
    </StyledSuffixButton>
  );
};

ShowHideValueToggleButton.propTypes = {
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** If true, button is disabled and value cannot be shown */
  isDisabled: PropTypes.bool,
  /** Current state in which component should be rendered */
  isHidden: PropTypes.bool,
  /** Callback which is called on toggle */
  onToggleShowHide: PropTypes.func.isRequired,
  /** Label shown when component's state is hidden */
  toggleHideLabel: PropTypes.node,
  /** Label shown when component's state is not hidden */
  toggleShowLabel: PropTypes.node,
};

export { ShowHideValueToggleButton };
